import { useMsal } from '@azure/msal-react';
import { Button, Field, ProgressBar, Spinner, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useStores } from '../../common/hooks';
import { OperationName, OperationNames, OperationStatus, RoleName } from '../../models';
import { ConfirmationDialog } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import NoAccess from '../shared-components/NoAccess';
import './OperationDashboardPage.scss';

interface OperationDialogContent {
  title: string;
  subtext: React.JSX.Element;
}

const operationDialogContent: Record<OperationName, OperationDialogContent> = {
  BulkExportNominations: {
    title: 'Confirm Initiate Bulk Export',
    subtext: (<p>Are you sure you want to initiate the bulk export of nominations? This action cannot be cancelled.</p>),
  },
  SyncProgramEnrolments: {
    title: 'Confirm Refresh Program Enrolments',
    subtext: (<p>Are you sure you want to refresh the PartnerGlobal program enrolment cache? This warms eligibility data in the background and may take a while.</p>),
  },
};

const OperationDashboardPageImpl: React.FC = () => {
  const {
    instance,
  } = useMsal();

  const {
    operationDashboardPageUiStore,
    apiStore,
    userStore,
  } = useStores();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pendingOperation, setPendingOperation] = useState<OperationName | undefined>(undefined);
  const streamControllersRef = useRef<Set<AbortController>>(new Set());

  const bulkExportOperationStatusProgress = operationDashboardPageUiStore.getOperationStatusProgress(OperationNames.BulkExportNominations);
  const syncEnrolmentsProgress = operationDashboardPageUiStore.getOperationStatusProgress(OperationNames.SyncProgramEnrolments);
  const syncSummary = operationDashboardPageUiStore.programEnrolmentSyncSummary;

  const loadSyncSummary = async (): Promise<void> => {
    try {
      const summary = await apiStore.getProgramEnrolmentSyncSummary(instance);
      if (summary) {
        operationDashboardPageUiStore.setProgramEnrolmentSyncSummary(summary);
      }
    } catch (error) {
      // TODO - Handle Error
    }
  };

  const subscribeToOperation = async (name: OperationName): Promise<void> => {
    const controller = new AbortController();
    streamControllersRef.current.add(controller);

    try {
      await apiStore.streamOperationStatus(instance, name, (status: OperationStatus) => {
        operationDashboardPageUiStore.setOperationStatus(name, status);

        if (!operationDashboardPageUiStore.isOperationStatusInProgress(name)) {
          if (name === OperationNames.SyncProgramEnrolments) {
            loadSyncSummary();
          }
          controller.abort();
        }
      }, controller.signal);
    } catch (error) {
      // Stream aborted (operation finished or page unmounted) or failed fatally.
    } finally {
      streamControllersRef.current.delete(controller);
    }
  };

  const handleConfirmInitiate = async (): Promise<void> => {
    if (!pendingOperation) return;

    const name = pendingOperation;
    setIsLoading(true);
    try {
      await apiStore.initiateOperation(instance, name);
      operationDashboardPageUiStore.markOperationInProgress(name);
      setPendingOperation(undefined);
    } catch (error) {
      // TODO - Handle Error
    } finally {
      setIsLoading(false);
    }

    subscribeToOperation(name);
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [bulkExportStatus, syncStatus] = await Promise.all([
        apiStore.getOperationStatus(instance, OperationNames.BulkExportNominations),
        apiStore.getOperationStatus(instance, OperationNames.SyncProgramEnrolments),
      ]);
      if (bulkExportStatus) {
        operationDashboardPageUiStore.setOperationStatus(OperationNames.BulkExportNominations, bulkExportStatus);
      }
      if (syncStatus) {
        operationDashboardPageUiStore.setOperationStatus(OperationNames.SyncProgramEnrolments, syncStatus);
      }
    } catch (error) {
      // TODO - Handle Error
    } finally {
      setIsLoading(false);
    }

    await loadSyncSummary();

    if (operationDashboardPageUiStore.isOperationStatusInProgress(OperationNames.BulkExportNominations)) {
      subscribeToOperation(OperationNames.BulkExportNominations);
    }
    if (operationDashboardPageUiStore.isOperationStatusInProgress(OperationNames.SyncProgramEnrolments)) {
      subscribeToOperation(OperationNames.SyncProgramEnrolments);
    }
  };

  useEffect(() => {
    preloadData();
    return () => {
      streamControllersRef.current.forEach((controller) => controller.abort());
      streamControllersRef.current.clear();
      operationDashboardPageUiStore.resetStates();
    };
  }, []);

  const isSyncInProgress = operationDashboardPageUiStore.isOperationStatusInProgress(OperationNames.SyncProgramEnrolments);
  const isBulkExportFailed = operationDashboardPageUiStore.isOperationStatusFailed(OperationNames.BulkExportNominations);
  const isSyncFailed = operationDashboardPageUiStore.isOperationStatusFailed(OperationNames.SyncProgramEnrolments);

  return userStore.isAuthorized([RoleName.Admin]) ? (
    <FlexBox
      isFullWidth
      as='main'
      direction='column'
      aligns='start'
      className='operation-dashboard-page'
    >
      <div>
        <Text
          id='bulk-export-nominations'
          block
          weight='semibold'
        >
          Bulk Export Nominations
        </Text>
        <Button
          appearance='primary'
          onClick={() => setPendingOperation(OperationNames.BulkExportNominations)}
          disabled={operationDashboardPageUiStore.isOperationStatusInProgress(OperationNames.BulkExportNominations) || isLoading}
          icon={isLoading ? <Spinner size='extra-tiny' /> : null}
        >
          Start
        </Button>
      </div>
      {
        operationDashboardPageUiStore.operationStatus.has(OperationNames.BulkExportNominations) && (
          <Field
            validationMessage={isBulkExportFailed ? 'Last run failed — please try again.' : `Progress ${bulkExportOperationStatusProgress ?? 0}%`}
            validationState={isBulkExportFailed ? 'error' : 'none'}
          >
            <ProgressBar
              title=''
              thickness='large'
              value={bulkExportOperationStatusProgress ? bulkExportOperationStatusProgress / 100 : 0}
              aria-labelledby='bulk-export-nominations'
            />
          </Field>
        )
      }
      <div>
        <Text
          id='refresh-program-enrolments'
          block
          weight='semibold'
        >
          Refresh Program Enrolments
        </Text>
        {
          syncSummary && (
            <Text block>
              {`PartnerGlobal accounts: ${syncSummary.syncedCount}/${syncSummary.totalCount} refreshed`}
            </Text>
          )
        }
        <Button
          appearance='primary'
          onClick={() => setPendingOperation(OperationNames.SyncProgramEnrolments)}
          disabled={isSyncInProgress || isLoading}
          icon={isLoading ? <Spinner size='extra-tiny' /> : null}
        >
          Start
        </Button>
      </div>
      {
        operationDashboardPageUiStore.operationStatus.has(OperationNames.SyncProgramEnrolments) && (
          <Field
            validationMessage={isSyncFailed ? 'Last run failed — please try again.' : `Progress ${syncEnrolmentsProgress ?? 0}%`}
            validationState={isSyncFailed ? 'error' : 'none'}
          >
            <ProgressBar
              title=''
              thickness='large'
              value={syncEnrolmentsProgress ? syncEnrolmentsProgress / 100 : 0}
              aria-labelledby='refresh-program-enrolments'
            />
          </Field>
        )
      }
      <ConfirmationDialog
        isOpen={pendingOperation !== undefined}
        isLoading={isLoading}
        dialogTitle={pendingOperation ? operationDialogContent[pendingOperation].title : ''}
        dialogSubtext={pendingOperation ? operationDialogContent[pendingOperation].subtext : undefined}
        onPrimaryButtonClick={() => handleConfirmInitiate()}
        onSecondaryButtonClick={() => setPendingOperation(undefined)}
        primaryButtonLabel='Yes'
        secondaryButtonLabel='No, go back'
        onCloseDialogClick={() => setPendingOperation(undefined)}
      />
    </FlexBox>
  ) : (
    <NoAccess />
  )
};

export const OperationDashboardPage = observer(OperationDashboardPageImpl);
